import React, { memo } from 'react';
import { motion } from 'framer-motion';
const RELEASES = [{
  version: '95',
  date: 'June 11, 2026',
  title: 'A smoother welcome',
  intro: "The screens you see when getting started with Giga are cleaner and more responsive — the welcome screen's connection flow has been polished, and Slack integration now registers instantly. The rest of this release is under-the-hood reliability work.",
  changes: ['Getting started: A more polished Slack connection flow and clearer workspace selection', 'Fixed OAuth redirects occasionally hanging on Safari', 'General stability and reliability improvements to the core event bus'],
  why: 'First impressions count — a cleaner, more responsive welcome experience makes getting going with Giga feel effortless.'
}, {
  version: '94',
  date: 'June 10, 2026',
  title: 'See the whole thread',
  intro: 'When Giga drafts a reply into a Slack thread, the preview now shows the whole conversation — the original message on top and every reply beneath it — so you can approve the reply with full context. Getting started is also one step shorter, and a fix unblocks older drafts that refused to send.',
  changes: ['Slack: Replies into a thread now show the full thread in the preview — the original message plus its replies, opened at the newest one — instead of a flat snippet', 'Approvals: The "Approve & Send" button now clearly indicates which channel the message will land in', 'Fixed an issue where drafts older than 24 hours would fail to send due to expired tokens'],
  why: "Context is everything. You shouldn't have to jump back into Slack just to verify what Giga is replying to."
}, {
  version: '93',
  date: 'May 28, 2026',
  title: 'Always-On Triggers',
  intro: 'You can now wire Giga directly to your webhooks and CRM events. Instead of waiting for you to ask, Giga can now react to events in real-time — like researching a new lead the moment they enter HubSpot.',
  changes: ['New "Triggers" panel in the dashboard for managing event-driven workflows', 'Native integrations added for HubSpot, Salesforce, and GitHub webhooks', 'Status indicators now show when Giga is actively processing a background trigger'],
  why: 'True automation happens in the background. This moves Giga from a reactive assistant to a proactive coworker.'
}, {
  version: '92',
  date: 'May 15, 2026',
  title: 'Better memory retrieval',
  intro: "Giga's memory center has been completely rebuilt. It now understands temporal context much better — meaning it knows the difference between a decision made yesterday versus a policy from last year.",
  changes: ['Memory: Upgraded the vector search pipeline to heavily weight recency and explicit decisions', 'Recaps: Morning briefings now automatically highlight conflicting information if old docs contradict new decisions', 'Fixed a bug where Giga would occasionally hallucinate Jira ticket numbers'],
  why: 'An AI coworker is only as good as its memory. Trust requires perfect recall of the latest facts.'
}, {
  version: '91',
  date: 'May 02, 2026',
  title: 'Enterprise-grade guardrails',
  intro: "We've rolled out a suite of new controls for workspace admins to ensure Giga only accesses what it should, and only acts when permitted.",
  changes: ['Admin: New RBAC controls for restricting which channels Giga can read', 'Security: Added support for requiring human-in-the-loop approval for any external email drafts', 'Audit: Full exportable logs of every action Giga has taken in the last 90 days'],
  why: 'As larger teams adopt Giga, IT needs absolute certainty that data boundaries are respected.'
}];
export function Changelog() {
  return <div className="min-h-screen bg-[#FAF8F4] pt-32 pb-24 font-body text-[#2B211A]" data-id="element-7199" data-mp="3sm60eqaudufe">
      <div className="max-w-[1024px] mx-auto px-6 md:px-10" data-id="element-7200" data-mp="1drwvanht82g">
        {/* Header */}
        <div className="mb-16 md:mb-24" data-id="element-7201" data-mp="6f8rwpjaq0b4">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-mono text-xs font-bold tracking-widest uppercase text-neutral-500 mb-4 block" data-id="element-7202" data-mp="6c36bk9vlgk5">
            Changelog
          </motion.span>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-6xl font-title font-bold tracking-tight leading-[1.1]" data-id="element-7203" data-mp="135ax022hid68">
            Latest Giga Updates
          </motion.h1>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative border-t border-dashed border-neutral-300" data-id="element-7204" data-mp="k7qi9ntfouzc">
        {/* Vertical Spine (Desktop only) */}
        <div className="hidden md:block absolute top-0 bottom-0 left-[240px] w-px border-l border-dashed border-neutral-300" data-id="element-7205" data-mp="y81aovae6hmm" />

        <div className="max-w-[1024px] mx-auto" data-id="element-7206" data-mp="u79120xlyapt">
          {RELEASES.map((release, i) => <motion.div key={release.version} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="relative flex flex-col md:flex-row border-b border-dashed border-neutral-300" data-id="element-7207" data-mp="29jtt715n1z05" data-mpi={i}>
              {/* Left Rail (Version & Date) */}
              <div className="md:w-[240px] shrink-0 px-6 md:px-10 py-8 md:py-16 flex items-start gap-4" data-id="element-7208" data-mp="284higut0yamb">
                <div className="flex items-center gap-3 mt-1" data-id="element-7209" data-mp="2vubyfl8n4fas">
                  <span className="font-title italic text-xl font-bold text-[#EA580C]" data-id="element-7210" data-mp="2vw55v2r2v3ju">
                    {release.version}
                  </span>
                  {/* The dot that sits on the spine on desktop */}
                  <div className="hidden md:block absolute left-[240px] -translate-x-1/2 w-2 h-2 rounded-full bg-[#EA580C]" data-id="element-7211" data-mp="2r3c14evlzv7r" />
                  <span className="font-mono text-xs font-bold text-[#2B211A]" data-id="element-7212" data-mp="3esefnduy57y1">
                    {release.date}
                  </span>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 px-6 md:px-10 pb-12 md:py-16 md:pl-16" data-id="element-7213" data-mp="3tek8iv500j5p">
                <h2 className="text-3xl md:text-4xl font-title font-bold mb-6" data-id="element-7214" data-mp="3og0kmp6ugxap">
                  {release.title}
                </h2>

                <p className="text-base md:text-lg text-neutral-600 leading-relaxed mb-8" data-id="element-7215" data-mp="3kddmy7nhsi5l">
                  {release.intro}
                </p>

                <div className="mb-8" data-id="element-7216" data-mp="3agr3nnatdz5o">
                  <span className="block text-sm font-medium text-neutral-500 mb-4" data-id="element-7217" data-mp="3ahsogjhmu00l">
                    What changed:
                  </span>
                  <ul className="space-y-3" data-id="element-7218" data-mp="35o7qgrg1h61e">
                    {release.changes.map((change, j) => <li key={j} className="flex items-start gap-3 text-neutral-600" data-id="element-7219" data-mp="30r7iuc3jjv86" data-mpi={j}>
                        <span className="mt-2 w-1 h-1 rounded-full bg-neutral-400 shrink-0" data-id="element-7220" data-mp="2wqxgraivdoyl" />
                        <span className="leading-relaxed" data-id="element-7221" data-mp="2rpq6n4t98dor">{change}</span>
                      </li>)}
                  </ul>
                </div>

                <p className="text-neutral-600 leading-relaxed" data-id="element-7222" data-mp="1294cuz9n493s">
                  {release.why}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </div>;
}